import { Component, OnInit } from '@angular/core';
import { fadeInUp } from '../../animations/route-animations';
import { CommonModule } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import { Observable } from 'rxjs';
import { CartService } from '../../services/cart.service';
import { PolicyModalComponent } from '../../components/shared/policy-modal/policy-modal';
import { AuthService } from '../../services/auth.service';
import { OrderService } from '../../services/order.service';
import { DoseColorService } from '../../services/dose-color.service';
import { LanguageService } from '../../services/language.service';
import { ProductLocalizationService } from '../../services/product-localization.service';
import { CategoryLocalizationService } from '../../services/category-localization.service';
import { CartItem, DosePurchaseEligibility, DoseType } from '../../interfaces/product.interface';
import { ProductService } from '../../services/product.service';
import { Firestore, doc, getDoc } from '@angular/fire/firestore';

@Component({
  selector: 'app-cart',
  imports: [CommonModule, RouterModule, PolicyModalComponent],
  templateUrl: './cart.html',
  styleUrl: './cart.scss',
  animations: [fadeInUp]
})
export class Cart implements OnInit {
  cartItems: CartItem[] = [];
  doseEligibility: DosePurchaseEligibility | null = null;
  subtotal = 0;
  discount = 0;
  total = 0;
  isLoading = false;
  errorMessage = '';
  successMessage = '';
  isLoggedIn = false;
  totalProducts :any = [];
  activeOrderStatus: string | null = null; // Store order status for active dose

  constructor(
    private cartService: CartService,
    private authService: AuthService,
    private orderService: OrderService,
    private doseColorService: DoseColorService,
    private languageService: LanguageService,
    private productLocalizationService: ProductLocalizationService,
    private categoryLocalizationService: CategoryLocalizationService,
    private router: Router,
    private productService: ProductService,
    private firestore: Firestore
  ) {}

  ngOnInit(): void {
    this.loadCartData();
    this.checkUserLogin();

     // Get total product count from ProductService
     this.productService.getProducts().subscribe(products => {
      this.totalProducts = products;
    });
  }

  loadCartData(): void {
    this.cartService.getCartItems().subscribe(items => {
      this.cartItems = items;
      this.calculateTotals();
      this.validateCartItems();
    });
  }

  checkUserLogin(): void {
    this.isLoggedIn = this.authService.isLoggedInSync();
    
    if (this.isLoggedIn) {
      const currentUser = this.authService.getCurrentUser();
      if (currentUser) {
        this.authService.checkDosePurchaseEligibility(currentUser.id).subscribe(async (eligibility) => {
          this.doseEligibility = eligibility;
          this.validateCartItems();
          
          // Check order status if there's an active dose
          if (eligibility.currentActiveDose) {
            await this.checkActiveOrderStatus(eligibility.currentActiveDose);
          } else {
            this.activeOrderStatus = null;
          }
        });
      }
    }
  }

  calculateTotals(): void {
    this.subtotal = this.cartItems.reduce((total, item) => 
      total + ((item.price ?? 0) * item.cartQuantity), 0
    );

    // Calculate combo discount if applicable
    this.discount = this.orderService.calculateComboDiscount(this.cartItems);
    this.total = this.subtotal - this.discount;
  }

  validateCartItems(): void {
    if (!this.isLoggedIn || !this.doseEligibility) return;

    const currentUser = this.authService.getCurrentUser();
    if (!currentUser) return;

    const doseNumbers = this.cartItems.map(item => item.doseNumber);
    
    this.orderService.canPurchaseDoses(currentUser.id, doseNumbers).subscribe(result => {
      if (!result.canPurchase) {
        this.errorMessage = result.message;
      } else {
        this.errorMessage = '';
      }
    });
  }

  updateQuantity(itemId: string, quantity: number): void {
    if (quantity <= 0) {
      this.removeItem(itemId);
      return;
    }

    this.cartService.updateQuantity(itemId, quantity);
    this.loadCartData();
  }

  removeItem(itemId: string, selectedCategoryId?: string): void {
    this.cartService.removeFromCart(itemId, undefined, selectedCategoryId);
    this.loadCartData();
    this.successMessage = 'Item removed from cart';
    setTimeout(() => this.successMessage = '', 3000);
  }

  clearCart(): void {
    this.cartService.clearCart();
    this.loadCartData();
    this.successMessage = 'Cart cleared';
    setTimeout(() => this.successMessage = '', 3000);
  }

  proceedToCheckout(): void {
    if (!this.isLoggedIn) {
      this.router.navigate(['/login'], { 
        queryParams: { returnUrl: '/checkout' } 
      });
      return;
    }

    if (this.cartItems.length === 0) {
      this.errorMessage = 'Your cart is empty';
      return;
    }

    if (this.errorMessage) {
      // There are validation errors, don't proceed
      return;
    }

    this.router.navigate(['/checkout']);
  }

  continueShopping(): void {
    this.router.navigate(['/products']);
  }

  getDoseColor(doseType: string): string {
    switch(doseType) {
      case 'green': return '#4CAF50';
      case 'yellow': return '#FFC107';
      case 'red': return '#F44336';
      default: return '#6b7280';
    }
  }

  getDoseDisplayName(doseType: string): Observable<string> {
    return this.doseColorService.getDoseDisplayName(doseType as DoseType);
  }

  isComboOrder(): boolean {
    return this.orderService.isComboEligible(this.cartItems);
  }

  getComboSavings(): number {
    return this.discount;
  }

  getOfferTitle(): string {
    return this.t('limitedOfferTitle');
  }

  getOfferDescription(): string {
    return this.t('limitedOfferDescription');
  }

  getDoseStatusIcon(doseNumber: number): string {
    const status = this.getDoseStatus(doseNumber);
    if (status.message.includes('Completed')) return '✅';
    if (status.message.includes('In progress')) return '⏳';
    if (status.message.includes('Available')) return '🟢';
    if (status.message.includes('Cannot purchase')) return '🔒';
    return '📋';
  }

  getItemTotal(item: CartItem): number {
    return (item.price ?? 0) * item.cartQuantity;
  }

  // Check order status for active dose
  async checkActiveOrderStatus(activeDose: any): Promise<void> {
    if (!activeDose || !activeDose.orderId) {
      this.activeOrderStatus = null;
      return;
    }

    try {
      const orderRef = doc(this.firestore, 'orderTable', activeDose.orderId);
      const orderDoc = await getDoc(orderRef);
      
      if (orderDoc.exists()) {
        const orderData = orderDoc.data();
        this.activeOrderStatus = orderData['status'] || 'pending';
      } else {
        this.activeOrderStatus = null;
      }
    } catch (error) {
      console.error('Error checking order status:', error);
      this.activeOrderStatus = null;
    }
  }

  // Get order status text
  getOrderStatusText(status: string): string {
    const statusMap: { [key: string]: string } = {
      'pending': this.t('orderStatusPending') || 'Order Pending',
      'confirmed': this.t('orderStatusConfirmed') || 'Order Confirmed',
      'shipped': this.t('orderStatusShipped') || 'Order Shipped',
      'delivered': this.t('orderStatusDelivered') || 'Order Delivered',
      'cancelled': this.t('orderStatusCancelled') || 'Order Cancelled'
    };
    return statusMap[status] || status;
  }

  // Get eligibility status for display
  getEligibilityStatus(): string {
    if (!this.doseEligibility) return this.t('checking');
    
    if (this.doseEligibility.currentActiveDose) {
      // If order is not delivered, show order status instead of days remaining
      if (this.activeOrderStatus && this.activeOrderStatus !== 'delivered') {
        const doseType = this.doseEligibility.currentActiveDose.doseType;
        const doseDisplayName = this.getSyncDoseDisplayName(doseType);
        const orderStatusText = this.getOrderStatusText(this.activeOrderStatus);
        return `${doseDisplayName} - ${orderStatusText}`;
      }

      // If delivered, show days remaining (counting from delivery)
      const doseType = this.doseEligibility.currentActiveDose.doseType;
      const daysRemaining = this.doseEligibility.currentActiveDose.daysRemaining;
      const doseDisplayName = this.getSyncDoseDisplayName(doseType);
      return this.t('currentlyTaking', { 
        doseName: doseDisplayName, 
        daysRemaining: daysRemaining.toString() 
      });
    }

    const completed = this.doseEligibility.completedDoses.length;
    const nextDose = this.getNextDoseInfo();
    
    if (completed === 0) {
      return this.t('readyToStartJourney');
    } else if (completed === 3) {
      return this.t('congratsAllDosesCompleted');
    } else {
      return this.t('progressDosesCompleted', { 
        completed: completed.toString(), 
        nextDose: nextDose 
      });
    }
  }

  // Synchronous fallback for dose display names
  private getSyncDoseDisplayName(doseType: DoseType): string {
    const names: { [key: string]: string } = {
      green: 'Dose One',
      yellow: 'Dose Two',
      red: 'Dose Three'
    };
    return names[doseType] || 'Unknown Dose';
  }

  // Get next dose information
  getNextDoseInfo(): string {
    if (!this.doseEligibility) return '';
    
    if (this.doseEligibility.canPurchaseDose1) {
      return this.t('nextDoseOne');
    } else if (this.doseEligibility.canPurchaseDose2) {
      return this.t('nextDoseTwo');
    } else if (this.doseEligibility.canPurchaseDose3) {
      return this.t('nextDoseThree');
    }
    
    return this.t('completeCurrentDose');
  }

  // Get progress percentage based on delivery date (start counting from delivery)
  getDoseProgressPercentage(): number {
    if (!this.doseEligibility) return 0;
    
    // Count completed doses (each contributes 1.0 progress)
    let progress = this.doseEligibility.completedDoses.length;
    
    // Add partial progress for active dose (only if delivered)
    if (this.doseEligibility.currentActiveDose) {
      // Only calculate progress if order is delivered
      if (this.activeOrderStatus === 'delivered') {
        const daysRemaining = this.doseEligibility.currentActiveDose.daysRemaining;
        const totalDays = 30; // 30 days per dose period
        
        // Ensure daysRemaining doesn't go negative or exceed totalDays
        const safeDaysRemaining = Math.max(0, Math.min(daysRemaining, totalDays));
        const daysCompleted = totalDays - safeDaysRemaining;
        
        // Calculate progress as percentage of the 30-day period
        const doseProgress = Math.max(0, Math.min(daysCompleted / totalDays, 1));
        progress += doseProgress;
      }
      // If not delivered, don't add any progress (shows 0% for that dose)
    }
    
    // Calculate percentage: progress out of total possible doses (3 doses)
    // Each dose contributes up to 1.0, so max progress is 3.0
    const percentage = (progress / 3) * 100;
    
    // Ensure percentage is between 0 and 100
    return Math.max(0, Math.min(percentage, 100));
  }

  // Get detailed dose status
  getDoseStatus(doseNumber: number): { status: string; color: string; message: string } {
    // Doses beyond 3 (e.g., Dose 4 - Liquid Formula) are locked until 1-3 are completed
    if (doseNumber > 3) {
      if (!this.doseEligibility) {
        return { status: 'unknown', color: '#9e9e9e', message: this.t('loading') };
      }
      const has1 = this.doseEligibility.completedDoses.includes('green');
      const has2 = this.doseEligibility.completedDoses.includes('yellow');
      const has3 = this.doseEligibility.completedDoses.includes('red');
      const canBuy = (!!this.doseEligibility.canPurchaseDoses && this.doseEligibility.canPurchaseDoses[doseNumber] === true) || (has1 && has2 && has3 && !this.doseEligibility.currentActiveDose);
      return canBuy
        ? { status: 'available', color: '#2196f3', message: this.t('availableForPurchase') }
        : { status: 'locked', color: '#9e9e9e', message: this.t('completePreviousDoses') };
    }

    if (!this.doseEligibility) {
      return { status: 'unknown', color: '#9e9e9e', message: this.t('loading') };
    }

    const completed = this.doseEligibility.completedDoses;
    const currentActive = this.doseEligibility.currentActiveDose;

    if (currentActive && currentActive.doseType === this.getDoseTypeByNumber(doseNumber)) {
      // Only show days remaining if order is delivered
      if (this.activeOrderStatus === 'delivered') {
        return { 
          status: 'active', 
          color: '#ff9800', 
          message: this.t('inProgressDaysLeft', { daysRemaining: currentActive.daysRemaining.toString() })
        };
      } else {
        // Show order status if not delivered
        const orderStatusText = this.activeOrderStatus 
          ? this.getOrderStatusText(this.activeOrderStatus)
          : this.t('orderPending') || 'Order Pending';
        return { 
          status: 'active', 
          color: '#ff9800', 
          message: orderStatusText
        };
      }
    }

    if (completed.includes(this.getDoseTypeByNumber(doseNumber))) {
      return { status: 'completed', color: '#4caf50', message: this.t('completed') };
    }

    if (this.canPurchaseDose(doseNumber)) {
      return { status: 'available', color: '#2196f3', message: this.t('availableForPurchase') };
    }

    return { status: 'locked', color: '#9e9e9e', message: this.t('completePreviousDoses') };
  }

  // Helper method to get dose type by number
  getDoseTypeByNumber(doseNumber: number): DoseType {
    // Only handle the 3 main doses (1-3), ignore other products like Liquid Formula
    switch (doseNumber) {
      case 1: return 'green';
      case 2: return 'yellow'; 
      case 3: return 'red';
      default: return 'green'; // Fallback to green for unknown numbers
    }
  }

  // Check if specific dose can be purchased
  canPurchaseDose(doseNumber: number): boolean {
    if (!this.doseEligibility) return false;
    
    // First check the new flexible approach
    if (this.doseEligibility.canPurchaseDoses && this.doseEligibility.canPurchaseDoses[doseNumber] !== undefined) {
      return this.doseEligibility.canPurchaseDoses[doseNumber];
    }
    
    // Fallback to legacy approach for backward compatibility
    switch (doseNumber) {
      case 1: return this.doseEligibility.canPurchaseDose1;
      case 2: return this.doseEligibility.canPurchaseDose2;
      case 3: return this.doseEligibility.canPurchaseDose3;
      default: {
        // For doseNumber > 3 require 1-3 completed and no active dose
        const completed = this.doseEligibility.completedDoses || [];
        const has1 = completed.includes('green');
        const has2 = completed.includes('yellow');
        const has3 = completed.includes('red');
        return has1 && has2 && has3 && !this.doseEligibility.currentActiveDose;
      }
    }
  }

  // Get warning message for invalid items
  getInvalidItemWarning(item: CartItem): string {
    if (!this.canPurchaseDose(item.doseNumber)) {
      // For legacy dose numbers 1-3, provide specific messages
      switch (item.doseNumber) {
        case 1: return this.t('alreadyPurchasedOrInProgress');
        case 2: return this.t('completeDoseOneFirst');
        case 3: return this.t('completeDoseOneTwoFirst');
        default: return this.t('cannotPurchaseDose', { doseNumber: item.doseNumber.toString() });
      }
    }
    return '';
  }

  // Navigate to login
  goToLogin(): void {
    this.router.navigate(['/login'], { 
      queryParams: { returnUrl: '/cart' } 
    });
  }

  // Navigate to registration
  goToRegister(): void {
    this.router.navigate(['/register']);
  }

  // Translation method
  t(key: string, params?: { [key: string]: string | number }): string {
    return this.languageService.t(key, params);
  }

  // Localized product name
  getLocalizedName(item: CartItem): string {
    return this.productLocalizationService.getLocalizedName(item);
  }

  // Localized product description
  getLocalizedDescription(item: CartItem): string {
    return this.productLocalizationService.getLocalizedDescription(item);
  }

  // Get display name with category (Product Name + Category Name + Option X)
  getDisplayNameWithCategory(item: CartItem): string {
    const productName = this.getLocalizedName(item);
    const categoryName = item.selectedCategoryName;
    
    if (categoryName) {
      // Capitalize first letter of each word in category name
      const formattedCategoryName = categoryName
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ');
      
      // Add option label if sub-product option is selected
      if (item.selectedSubProductIndex !== undefined && item.selectedSubProductIndex >= 0) {
        const optionLabel = this.getOptionLabel(item.selectedSubProductIndex);
        return `${productName} (${formattedCategoryName}, ${optionLabel})`;
      }
      
      return `${productName} (${formattedCategoryName})`;
    }
    return productName;
  }

  // Get option label (Option A, Option B, etc.)
  getOptionLabel(index: number): string {
    return `Option ${String.fromCharCode(65 + index)}`;
  }

  // Get sub-products for display (items in box) - show selected option text
  getSubProducts(item: CartItem): string {
    // If selected sub-product text exists, use it (this is the actual data for DB)
    if (item.selectedSubProductText) {
      return item.selectedSubProductText;
    }
    
    // Fallback to array of sub-products (backward compatibility)
    const subProducts: any = item.selectedCategorySubProducts;
    if (subProducts) {
      // Check if it's an array
      if (Array.isArray(subProducts) && subProducts.length > 0) {
        return subProducts.join(', ');
      }
      // If it's a string, return it directly
      if (typeof subProducts === 'string' && subProducts.length > 0) {
        return subProducts;
      }
    }
    return '';
  }

  // Get original price for item
  getItemOriginalPrice(item: CartItem): number | undefined {
    return item.originalPrice;
  }

  // Get unit price for item (not total)
  getItemUnitPrice(item: CartItem): number {
    return item.price ?? 0;
  }

  // Get unit original price for item (not total)
  getItemUnitOriginalPrice(item: CartItem): number | undefined {
    return item.originalPrice;
  }

  // Check if item has offer price (original price > current price)
  hasOfferPrice(item: CartItem): boolean {
    const originalPrice = item.originalPrice;
    const currentPrice = item.price ?? 0;
    
    // Debug log to check values
    if (originalPrice !== undefined && originalPrice > 0) {
      console.log('Price check:', {
        originalPrice,
        currentPrice,
        hasOffer: originalPrice > currentPrice,
        itemId: item.id
      });
    }
    
    return originalPrice !== undefined && originalPrice !== null && originalPrice > 0 && originalPrice > currentPrice;
  }

  // Localized product features
  getLocalizedFeatures(item: CartItem): string[] {
    return this.productLocalizationService.getLocalizedFeatures(item);
  }

  // Get localized product name by index for dose indicators
  getProductNameByIndex(index: number): string {
    if (this.totalProducts && this.totalProducts[index]) {
      return this.productLocalizationService.getLocalizedName(this.totalProducts[index]);
    }
    return '';
  }

  // Get the first image from cart item handling both array and object formats
  getFirstImage(item: CartItem): string {
    if (!item.image) return '/assets/images/default-product.png';
    
    // Handle both array and object formats
    const imageArray = Array.isArray(item.image) 
      ? item.image 
      : Object.values(item.image);
    
    const firstImage = imageArray[0];
    return (typeof firstImage === 'string') ? firstImage : '/assets/images/default-product.png';
  }

  // Get proper image URL handling both Firebase Storage and local paths
  getImageUrl(imagePath: string): string {
    if (!imagePath || imagePath === '' || imagePath === null || imagePath === undefined) {
      return '/assets/images/default-product.png';
    }
    
    // Firebase Storage URL (full URL)
    if (imagePath.startsWith('https://firebasestorage.googleapis.com/')) {
      return imagePath;
    }
    
    // Full URL or data URL
    if (imagePath.startsWith('http') || imagePath.startsWith('data:')) {
      return imagePath;
    }
    
    // Firebase Storage path (relative path/ike 'product-images/filename.jpg')
    if (imagePath.startsWith('product-images/')) {
      // For Firebase Storage paths, we need to construct the full URL
      // This is a limitation - in production you'd use Firebase Storage SDK to get the URL
      return '/assets/images/default-product.png'; // Fallback for now
    }
    
    // Local path - ensure it starts with ./
    if (imagePath.startsWith('./product/') || imagePath.startsWith('/product/')) {
      return imagePath.startsWith('./') ? imagePath : './' + imagePath.substring(1);
    }
    
    return '/assets/images/default-product.png';
  }

  // Handle image loading errors
  onImageError(event: Event): void {
    const img = event.target as HTMLImageElement;
    if (img) {
      img.src = '/assets/images/default-product.png';
    }
  }
}
